import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline, Snackbar, Alert } from '@mui/material';
import AppRoutes from './AppRoutes';
import Footer from './components/Footer';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import { BreadcrumbProvider } from './components/BreadcrumbContext';
import { ShoppingModeProvider } from './components/ShoppingModeContext';
import { TutorialProvider } from './components/TutorialProvider';
import { getTheme } from './theme';

const App: React.FC = () => {
  const [logoutAlertOpen, setLogoutAlertOpen] = useState(false);

  // State to manage the current theme mode (light or dark)
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      // User has manually set a preference - use it
      return savedMode === 'dark' ? 'dark' : 'light';
    }
    // No manual preference - detect device preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });

  // Track if user has a manual theme preference
  const [hasManualThemePreference, setHasManualThemePreference] = useState(
    () => localStorage.getItem('themeMode') !== null
  );

  // Listen for device color scheme changes (only if no manual preference)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a manual preference
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Function to toggle between light and dark themes
  const toggleTheme = () => {
    setMode((prev) => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
    setHasManualThemePreference(true);
  };

  // Function to reset theme to follow device preference
  const resetThemeToSystem = () => {
    localStorage.removeItem('themeMode');
    setHasManualThemePreference(false);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setMode(prefersDark ? 'dark' : 'light');
  };

  // Function to handle the logout alert display
  const handleLogoutAlert = () => {
    setLogoutAlertOpen(true);
    setTimeout(() => {
      setLogoutAlertOpen(false); // Automatically close the alert after 4 seconds
    }, 4000);
  };

  const theme = getTheme(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Ensures consistent styling across browsers */}
      <ShoppingModeProvider>
        <TutorialProvider>
        <BreadcrumbProvider> {/* Provides breadcrumb context to the app */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Header toggleTheme={toggleTheme} currentMode={mode} onLogout={handleLogoutAlert} resetThemeToSystem={resetThemeToSystem} hasManualThemePreference={hasManualThemePreference} />
            <Box component="main" sx={{ flex: 1, mb: 5 }}>
              <AppRoutes /> {/* Defines the application's routes */}
            </Box>
            <Footer />
          </Box>
          <BottomNav />
        </BreadcrumbProvider>
        </TutorialProvider>
      </ShoppingModeProvider>
      <Snackbar
        open={logoutAlertOpen}
        autoHideDuration={5000}
        onClose={() => setLogoutAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setLogoutAlertOpen(false)} severity="info" sx={{ width: '100%' }}>You have been logged out</Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default App;